import { Helmet } from "react-helmet-async";
import { useTranslations } from "@/hooks/useTranslations";

interface SEOProps {
  title?: string;
  description?: string;
  image?: string;
  url?: string;
  type?: string;
  noindex?: boolean;
  children?: React.ReactNode;
}

const SITE_NAME = "Union Collection";
const DEFAULT_TITLE = "Union Collection | Japanese Inspired Minimal Streetwear";
const DEFAULT_DESCRIPTION = "Union Collection — home of the Union Waistband, our signature internal-belt design. Japanese-inspired minimal streetwear built on presence, not noise.";
const DEFAULT_IMAGE = "https://unioncollection.net/og-image.png";
const BASE_URL = "https://unioncollection.net";

const LOCALE_MAP: Record<string, string> = {
  en: "en_US",
  ja: "ja_JP",
  ko: "ko_KR",
};

export function SEO({
  title,
  description = DEFAULT_DESCRIPTION,
  image = DEFAULT_IMAGE,
  url,
  type = "website",
  noindex = false,
  children,
}: SEOProps) {
  const { language } = useTranslations();
  const fullTitle = title ? `${title} | ${SITE_NAME}` : DEFAULT_TITLE;
  const canonical = url ? `${BASE_URL}${url}` : undefined;
  const ogLocale = LOCALE_MAP[language] || "en_US";

  return (
    <Helmet htmlAttributes={{ lang: language || "en" }}>
      <title>{fullTitle}</title>
      <meta name="description" content={description} />
      {noindex && <meta name="robots" content="noindex, nofollow" />}

      {/* Open Graph */}
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:type" content={type} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={`${SITE_NAME} — Japanese Inspired Minimal Streetwear`} />
      <meta property="og:locale" content={ogLocale} />
      {canonical && <meta property="og:url" content={canonical} />}
      <meta property="og:site_name" content={SITE_NAME} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:image:alt" content={`${SITE_NAME} — Japanese Inspired Minimal Streetwear`} />

      {/* Canonical */}
      {canonical && <link rel="canonical" href={canonical} />}

      {/* Hreflang — single English variant + x-default until real translated URLs exist */}
      {canonical && <link rel="alternate" hrefLang="en" href={canonical} />}
      {canonical && <link rel="alternate" hrefLang="x-default" href={canonical} />}

      {children}
    </Helmet>
  );
}

export function OrganizationJsonLd() {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: "Union Collection",
    alternateName: "Union Collection — Japanese Inspired Minimal Streetwear",
    url: "https://unioncollection.net",
    logo: "https://unioncollection.net/favicon.png",
    image: "https://unioncollection.net/og-image.png",
    description:
      "Union Collection is a Japanese-inspired minimal streetwear brand. Home of the Union Waistband — our signature internal-belt design. Built on presence, not noise.",
    slogan: "Built on Presence",
    foundingLocation: {
      "@type": "Place",
      name: "Middleton, Wisconsin, United States",
    },
    areaServed: {
      "@type": "Country",
      name: "United States",
    },
    knowsAbout: [
      "Union Waistband",
      "Japanese streetwear",
      "Minimal streetwear",
      "Internal belt waistband design",
    ],
    sameAs: [
      "https://www.instagram.com/unioncollection.net",
      "https://x.com/Collection44937",
      "https://www.pinterest.com/unioncollection",
    ],
    contactPoint: {
      "@type": "ContactPoint",
      contactType: "customer service",
      email: "support@unioncollection.net",
      url: "https://unioncollection.net/contact",
      areaServed: "US",
      availableLanguage: ["English", "Japanese", "Korean"],
    },
  };

  const websiteData = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    name: "Union Collection",
    url: "https://unioncollection.net",
    potentialAction: {
      "@type": "SearchAction",
      target: "https://unioncollection.net/collection?search={search_term_string}",
      "query-input": "required name=search_term_string",
    },
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      <script type="application/ld+json">{JSON.stringify(websiteData)}</script>
    </Helmet>
  );
}

interface BreadcrumbItem {
  name: string;
  url: string;
}

export function BreadcrumbJsonLd({ items }: { items: BreadcrumbItem[] }) {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: item.name,
      item: `https://unioncollection.net${item.url}`,
    })),
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
    </Helmet>
  );
}

interface ProductJsonLdProps {
  name: string;
  description?: string;
  image?: string;
  price: number;
  currency?: string;
  availability?: "InStock" | "OutOfStock" | "PreOrder";
  sku?: string;
  rating?: number;
  reviewCount?: number;
  url: string;
}

export function ProductJsonLd({
  name,
  description,
  image,
  price,
  currency = "USD",
  availability = "InStock",
  sku,
  rating,
  reviewCount,
  url,
}: ProductJsonLdProps) {
  const availabilityMap = {
    InStock: "https://schema.org/InStock",
    OutOfStock: "https://schema.org/OutOfStock",
    PreOrder: "https://schema.org/PreOrder",
  };

  // priceValidUntil: 1 year from today (rich result requirement)
  const priceValidUntil = new Date();
  priceValidUntil.setFullYear(priceValidUntil.getFullYear() + 1);
  const priceValidUntilStr = priceValidUntil.toISOString().split("T")[0];

  const structuredData: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "Product",
    name,
    description: description || `${name} from Union Collection`,
    image: image || "https://unioncollection.net/og-image.png",
    url: `https://unioncollection.net${url}`,
    itemCondition: "https://schema.org/NewCondition",
    brand: {
      "@type": "Brand",
      name: "Union Collection",
    },
    offers: {
      "@type": "Offer",
      price: (price / 100).toFixed(2),
      priceCurrency: currency,
      priceValidUntil: priceValidUntilStr,
      availability: availabilityMap[availability],
      itemCondition: "https://schema.org/NewCondition",
      url: `https://unioncollection.net${url}`,
      seller: {
        "@type": "Organization",
        name: "Union Collection",
      },
      hasMerchantReturnPolicy: {
        "@type": "MerchantReturnPolicy",
        applicableCountry: "US",
        returnPolicyCategory: "https://schema.org/MerchantReturnFiniteReturnWindow",
        merchantReturnDays: 30,
        returnMethod: "https://schema.org/ReturnByMail",
        returnFees: "https://schema.org/FreeReturn",
      },
      shippingDetails: {
        "@type": "OfferShippingDetails",
        shippingRate: {
          "@type": "MonetaryAmount",
          value: "0",
          currency: "USD",
        },
        shippingDestination: {
          "@type": "DefinedRegion",
          addressCountry: "US",
        },
        deliveryTime: {
          "@type": "ShippingDeliveryTime",
          handlingTime: {
            "@type": "QuantitativeValue",
            minValue: 1,
            maxValue: 2,
            unitCode: "DAY",
          },
          transitTime: {
            "@type": "QuantitativeValue",
            minValue: 2,
            maxValue: 5,
            unitCode: "DAY",
          },
        },
      },
    },
  };

  if (sku) {
    structuredData.sku = sku;
  }

  if (rating && reviewCount && reviewCount > 0) {
    structuredData.aggregateRating = {
      "@type": "AggregateRating",
      ratingValue: rating.toFixed(1),
      reviewCount,
      bestRating: "5",
      worstRating: "1",
    };
  }

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
    </Helmet>
  );
}

interface CollectionItemListItem {
  id: string;
  name: string;
  image?: string;
  price?: number;
}

export function CollectionItemListJsonLd({
  items,
  url,
  name = "Union Collection",
}: {
  items: CollectionItemListItem[];
  url: string;
  name?: string;
}) {
  if (!items.length) return null;

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name,
    url: `https://unioncollection.net${url}`,
    numberOfItems: items.length,
    itemListElement: items.slice(0, 30).map((item, index) => ({
      "@type": "ListItem",
      position: index + 1,
      url: `https://unioncollection.net/product/${item.id}`,
      name: item.name,
      ...(item.image ? { image: item.image } : {}),
    })),
  };

  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
    </Helmet>
  );
}
